import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, STATUS_LABELS, STATUS_COLORS } from "../../lib/api";
import { Package, ChevronRight, Clock, ArrowRight, AlertCircle, Sparkles } from "lucide-react";
import ReferralCard from "./ReferralCard";

function Countdown({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - now;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hrs = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (ms < 0) return <span className="text-red-600 text-xs font-semibold">⚠ {Math.abs(days)}d overdue</span>;
  return <span className="text-[#F7941D] text-xs font-semibold flex items-center gap-1"><Clock className="w-3 h-3" />{days}d {hrs}h remaining</span>;
}

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/my-orders").then(r => { setOrders(r.data); setLoading(false); });
  }, []);

  if (loading) return <div className="bg-white rounded-2xl p-12 text-center text-gray-500">Loading orders…</div>;

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-[#1a3c5e]/10">
        <Package className="w-12 h-12 mx-auto text-[#F7941D] mb-4" />
        <h2 className="font-display text-xl font-bold text-[#1a3c5e]">No orders yet</h2>
        <p className="text-sm text-gray-500 mt-1 mb-5">Get started with your first design package.</p>
        <Link to="/services">
          <button className="px-6 py-3 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl font-semibold inline-flex items-center gap-2" data-testid="empty-order-btn">
            Order Now <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    );
  }

  const hasCompleted = orders.some(o => o.status === "completed");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">My Orders</h2>
        <Link to="/services" className="text-sm font-semibold text-[#F7941D] hover:underline">+ New Order</Link>
      </div>
      <ReferralCard />
      {hasCompleted && (
        <Link to="/services" data-testid="cross-sell-prompt">
          <div className="bg-[#FEF3E2] border border-[#F7941D]/30 rounded-2xl p-4 flex items-center gap-3 hover-lift">
            <Sparkles className="w-5 h-5 text-[#F7941D] shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-[#1a3c5e] text-sm">Loved your design? Explore more of our services</div>
              <div className="text-xs text-gray-600">3D elevation, interior design, construction services and more.</div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#1a3c5e]/40 shrink-0" />
          </div>
        </Link>
      )}
      {orders.map(o => (
        <Link key={o.id} to={`/dashboard/orders/${o.id}`} data-testid={`order-row-${o.id}`}>
          <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5 hover-lift">
            <div className="flex flex-wrap justify-between gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-700"}`}>
                    {STATUS_LABELS[o.status] || o.status}
                  </span>
                  {(o.status === "requirements_pending" || o.pending_approvals > 0) && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1" data-testid={`action-needed-${o.id}`}>
                      <AlertCircle className="w-3 h-3" />
                      {o.status === "requirements_pending" ? "Requirements needed" : `${o.pending_approvals} drawing${o.pending_approvals > 1 ? "s" : ""} to review`}
                    </span>
                  )}
                  {o.status !== "pending_payment" && o.status !== "completed" && o.deadline && <Countdown deadline={o.deadline} />}
                </div>
                <div className="font-display text-lg font-bold text-[#1a3c5e]">{o.service_name}</div>
                <div className="text-xs text-gray-500 mt-1">Order #{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="font-display text-xl font-bold text-[#F7941D]">{inr(o.amount)}</div>
                  {o.discount > 0 && <div className="text-[10px] text-emerald-600">Saved {inr(o.discount)}</div>}
                </div>
                <ChevronRight className="w-5 h-5 text-[#1a3c5e]/40" />
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
